<script setup lang="ts">
import { computed, h, ref, toRef, type VNodeChild } from 'vue'
import { NButton, NDropdown, NInput, NModal, NPopconfirm, type DropdownOption } from 'naive-ui'
import type { WikiPageDetail } from '../stores/wiki_pages'
import { useArticleActions } from '../composables/useArticleActions'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'
import ShareCreateModal from './ShareCreateModal.vue'

// The admin-actions half of WikiPageMeta.vue (#1097) — desktop
// Share/Edit/Rename/Export/Delete buttons, the mobile ⋯ dropdown, and
// the rename/delete/share modals behind them. Extracted so the parent
// stays under the 300-line hard limit; no behavior change from the
// inline version. Only ever mounted by the parent when `isAdmin`, so
// this component doesn't need its own admin gate.
const props = defineProps<{
  page: WikiPageDetail
}>()

const emit = defineEmits<{
  (e: 'cleared'): void
}>()

// Short-lived public share link — admin-only.
const shareModalOpen = ref(false)

// `toRef(props, 'page')` keeps the composable subscribed to live prop
// updates (ActionCable broadcasts can swap page identity while the
// viewer is open).
const { deleting, editSelected, deleteSelected, onExportSelect, exportMenu, renaming, renameSelected } =
  useArticleActions(toRef(props, 'page'), () => emit('cleared'))

// Rename modal state lives in the view so the composable stays
// transport-only. Open + draft path live here; the composable owns the
// loading flag and the API call. Pre-fill with the current path so the
// admin can edit the slug in place instead of retyping the whole thing.
const renameModalOpen = ref(false)
const renameDraft = ref('')
// Return focus to the trigger (Rename button / mobile ⋯ menu) on close.
useRestoreFocusOnClose(renameModalOpen)
function openRenameModal(): void {
  renameDraft.value = props.page.path
  renameModalOpen.value = true
}

// "Rename" enables only when the draft is a non-empty, distinct value.
// Server-side validation (PathValidator) is the real source of truth —
// this just keeps the obvious no-ops from queueing a job that would
// 422 on arrival.
const renameSubmittable = computed(
  () => renameDraft.value.trim().length > 0 && renameDraft.value.trim() !== props.page.path,
)

async function confirmRename(): Promise<void> {
  if (!renameSubmittable.value) return
  renameModalOpen.value = false
  await renameSelected(renameDraft.value.trim())
}

// Mobile (< sm) collapses Edit / Export / Delete behind a single ⋯
// hamburger using the NDropdown pattern
// mobile-menu — three buttons + the breadcrumb don't fit on a 390px
// phone otherwise. Desktop keeps the discrete buttons (more
// affordable for one-click work).
type MobileActionKey = 'share' | 'edit' | 'rename' | 'export-markdown' | 'export-pdf' | 'delete'

const mobileActions: { label: string; key: MobileActionKey }[] = [
  { label: 'Share', key: 'share' },
  { label: 'Edit', key: 'edit' },
  { label: 'Rename', key: 'rename' },
  { label: 'Export → Markdown', key: 'export-markdown' },
  { label: 'Export → PDF', key: 'export-pdf' },
  { label: 'Delete', key: 'delete' },
]

// Tints the "Delete" item red so the destructive action stands out
// from the navigation/export entries above it. Other items render as
// plain labels — Naive UI's `render-label` contract takes the widest
// option type; we narrow at the boundary because every option here
// comes from `mobileActions`.
function renderMobileAction(option: DropdownOption): VNodeChild {
  const item = option as { label: string; key: MobileActionKey }
  if (item.key === 'delete') {
    return h('span', { class: 'text-red-300' }, item.label)
  }
  return item.label
}

// Delete confirm on mobile uses NModal (preset="dialog") instead of
// the desktop NPopconfirm — the popconfirm anchors to its trigger,
// which is hidden inside the dropdown by the time the user taps
// Delete. NModal is self-mounting so we don't need NDialogProvider
// at the app root.
const mobileDeleteOpen = ref(false)
useRestoreFocusOnClose(mobileDeleteOpen)

function onMobileActionSelect(key: MobileActionKey): void {
  switch (key) {
    case 'share':
      shareModalOpen.value = true
      break
    case 'edit':
      editSelected()
      break
    case 'rename':
      openRenameModal()
      break
    case 'export-markdown':
      onExportSelect('markdown')
      break
    case 'export-pdf':
      onExportSelect('pdf')
      break
    case 'delete':
      mobileDeleteOpen.value = true
      break
  }
}

function confirmMobileDelete(): void {
  mobileDeleteOpen.value = false
  deleteSelected()
}
</script>

<template>
  <!-- Desktop admin actions: three discrete buttons. Hidden on
       mobile (< sm) — the hamburger below replaces them so the
       breadcrumb + actions fit on a 390px phone. e2e tests run on
       desktop viewport, so the testids stay reachable here. -->
  <div class="hidden sm:flex items-center gap-1.5 shrink-0">
    <NButton quaternary size="small" data-testid="share-page" @click="shareModalOpen = true">Share</NButton>
    <NButton quaternary size="small" data-testid="edit-page" @click="editSelected">Edit</NButton>
    <NButton quaternary size="small" :loading="renaming" data-testid="rename-page" @click="openRenameModal"
      >Rename</NButton
    >
    <NDropdown trigger="click" :options="exportMenu" @select="onExportSelect">
      <NButton quaternary size="small" data-testid="export-page">Export</NButton>
    </NDropdown>
    <NPopconfirm
      positive-text="Delete"
      negative-text="Cancel"
      :positive-button-props="{ type: 'error' }"
      @positive-click="deleteSelected"
    >
      <template #trigger>
        <NButton quaternary size="small" type="error" :loading="deleting" data-testid="delete-page">Delete</NButton>
      </template>
      <!-- &nbsp; on either side of the <code> element so naive-ui's
           popconfirm slot doesn't collapse the surrounding spaces and
           render "Deleteentities/foo.mdand rewrite". -->
      Delete&nbsp;<code class="font-mono">{{ page.path }}</code
      >&nbsp;and rewrite every inbound markdown link? This is irreversible apart from a git revert.
    </NPopconfirm>
  </div>

  <!-- Mobile admin actions: ⋯ hamburger that opens the same actions
       as a dropdown. Same NDropdown / render-label / select-handler
       dropdown pattern so the two surfaces feel
       consistent. The :loading binding on the trigger surfaces
       delete progress (the modal closes immediately on confirm; the
       spinner sits on the hamburger until the API responds). -->
  <NDropdown trigger="click" :options="mobileActions" :render-label="renderMobileAction" @select="onMobileActionSelect">
    <NButton
      quaternary
      size="small"
      class="!flex sm:!hidden shrink-0 min-h-[44px]"
      :loading="deleting"
      data-testid="page-actions-menu"
      aria-label="Page actions"
      >⋯</NButton
    >
  </NDropdown>

  <!-- Mobile delete confirm. NModal preset="dialog" matches the
       desktop NPopconfirm visually (red Delete button, Cancel) and
       self-mounts via the global teleport so we don't need an
       NDialogProvider at the app root. -->
  <NModal
    v-model:show="mobileDeleteOpen"
    preset="dialog"
    type="error"
    title="Delete this page?"
    positive-text="Delete"
    negative-text="Cancel"
    :positive-button-props="{ type: 'error' }"
    @positive-click="confirmMobileDelete"
  >
    Delete&nbsp;<code class="font-mono">{{ page.path }}</code
    >&nbsp;and rewrite every inbound markdown link? This is irreversible apart from a git revert.
  </NModal>

  <!-- Rename modal — shared between desktop and mobile because the
       input field doesn't fit cleanly inside an NPopconfirm. The
       positive button is disabled until the draft path is non-empty
       and distinct from the current path; server-side PathValidator
       is the authoritative gate. -->
  <NModal
    v-model:show="renameModalOpen"
    preset="dialog"
    title="Rename this page?"
    positive-text="Rename"
    negative-text="Cancel"
    :positive-button-props="{ disabled: !renameSubmittable }"
    @positive-click="confirmRename"
  >
    <div class="flex flex-col gap-2">
      <p>
        Repoint every inbound markdown link to the new path and tombstone the old one. The old path stays in git history
        with a&nbsp;<code class="font-mono">superseded-by:</code>&nbsp;marker.
      </p>
      <NInput v-model:value="renameDraft" placeholder="entities/sarah-chen.md" data-testid="rename-page-input" />
    </div>
  </NModal>

  <!-- Short-lived public share link. Admin-only; opened from the
       Share action (desktop button or mobile menu). -->
  <ShareCreateModal v-model:show="shareModalOpen" :page="page" />
</template>
